import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Instagram, ExternalLink, MapPin, Sparkles, BookOpen, Heart, Play } from 'lucide-react';

export const AboutPage = ({ onBack, onHome }) => {
  return (
    <motion.div
      className="min-h-screen p-6 pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <button
        onClick={onHome}
        className="fixed top-6 right-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all flex items-center gap-2"
      >
        <Home className="w-5 h-5" />
        <span className="text-sm">Home</span>
      </button>
      
      <div className="max-w-3xl mx-auto pt-20 space-y-10">
        {/* Header */}
        <div className="text-center space-y-4">
          <motion.div
            className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center border border-violet-500/30 overflow-hidden"
            whileHover={{ scale: 1.05 }}
          >
            <Sparkles className="w-14 h-14 text-violet-400" />
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">
            Hatice Akan Gönen
          </h1>
          <p className="text-violet-400 text-sm">
            Academy of Spirits Kurucusu
          </p>
        </div>
        
        {/* About Section */}
        <div className="glass-panel p-6 space-y-6">
          <h2 className="text-xl font-heading text-cyan-400 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Hakkımda
          </h2>
          
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Merhaba, ben Hatice Akan Gönen. Ruhlar Akademisi'nin kurucusuyum. 
              Akademi, bir rüyamda kanallık bilgisi ile geldi ve 2020'nin başlarında 
              durugörü ile gördüğüm bu vizyonun temellerini pandemi başında attım.
            </p>
            
            <p>
              Akademimiz ilk başta Telegram üzerinden canlı sohbetlerle ilerledi. 
              Çalışmalarıma eğitimler şeklinde devam etmeye karar verdim ve eğitimime 
              katılacak öğrencilerimi psişik test ile özenle seçtim.
            </p>
            
            <p>
              İleri seviye psişik sınıfımızda kriminal vakalar ve hastalıkların ruhsal 
              tespiti ve tedavisi, telepatik iletişim, öte alemle bağlantılar, kanallık 
              bilgisi, medyumik yetenekleri şifa için kullanmaya yönelik çalışmalar yapıyoruz.
            </p>
            
            <p>
              Ankara İlahiyat Fakültesi'nde yüksek lisans eğitimim ve şamanizm konulu 
              tez çalışmam devam ederken bir taraftan da 6 aylık kapsamlı 
              'Durugörü Geliştirme Eğitimi' vermeye devam ediyorum.
            </p>
            
            <p>
              Tayland'a taşındıktan sonra farklı ekollerden eğitimler aldım ve 
              Mantak Chia'nın Qi Enerjisi üzerine yoğunlaştım. Her birimizin psişik 
              yeteneklerle doğduğu ve zamanla köreldiği fakat tekrar bu yetenekleri 
              kazanabileceğimize inancım tam.
            </p>
          </div>
        </div>
        
        {/* Services */}
        <div className="glass-panel p-6 space-y-6">
          <h2 className="text-xl font-heading text-cyan-400 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Danışmanlık Hizmetleri
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'Aura Analizi',
              'Crescent Healing Technique (CHTQ)',
              'Jean Adrienne Arınma Sistemi (JAAS)',
              'Durugörü ile Tarot Rehberliği',
              'Şamanik Regresyon',
              'Chi Enerjisi Çalışmaları'
            ].map((service) => (
              <div 
                key={service}
                className="flex items-center gap-3 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20"
              >
                <span className="w-2 h-2 rounded-full bg-violet-400" />
                <span className="text-slate-300 text-sm">{service}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Bali Programs - More Prominent */}
        <div className="glass-panel p-6 space-y-6 border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 to-orange-500/10">
          <h2 className="text-2xl font-heading text-amber-400 flex items-center gap-2">
            <MapPin className="w-6 h-6" />
            Bali'de Yüz Yüze Eğitimler
          </h2>
          
          <div className="space-y-4 text-slate-300">
            <p className="text-lg">
              <strong className="text-amber-300">Şu anda Bali'de yüz yüze durugörü ve psişik yetenekleri 
              geliştirme eğitimleri vermekteyim.</strong>
            </p>
            
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
              <p className="text-amber-200 text-sm">
                Bali retreat programlarımıza katılarak yoğun bir şekilde psişik yeteneklerinizi 
                geliştirme fırsatı yakalayabilirsiniz. Grup ve bireysel seanslar mevcuttur.
              </p>
            </div>
            
            <p className="text-slate-400">
              Bali'deki programlara katılmak veya detaylı bilgi almak için Instagram üzerinden 
              iletişime geçebilirsiniz:
            </p>
            
            <a
              href="https://instagram.com/liliumakan"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-pink-500/30 to-purple-500/30 border-2 border-pink-500/50 text-pink-300 hover:from-pink-500/40 hover:to-purple-500/40 transition-all text-lg font-medium"
            >
              <Instagram className="w-6 h-6" />
              @liliumakan
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
        
        {/* YouTube Video */}
        <div className="glass-panel p-6 space-y-4">
          <h2 className="text-xl font-heading text-red-400 flex items-center gap-2">
            <Play className="w-5 h-5" />
            Video
          </h2>
          
          <div className="aspect-video rounded-xl overflow-hidden border border-slate-700">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/zQtKvx2zG8c"
              title="Hatice Akan Gönen"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
        
        {/* Podcast */}
        <div className="glass-panel p-6 space-y-4">
          <h2 className="text-xl font-heading text-green-400 flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Podcast
          </h2>
          
          <p className="text-slate-300">
            Kendimden ve spiritüel yolculuğumdan detaylıca bahsettiğim podcast 
            bölümüne <strong className="text-green-400">"Benden Ötesi"</strong> yazarak 
            Spotify'da aratarak ulaşabilirsiniz. Keyifli dinlemeler.
          </p>
        </div>
        
        {/* Social Links */}
        <div className="glass-panel p-6 space-y-4">
          <h2 className="text-xl font-heading text-cyan-400">İletişim</h2>
          
          <div className="flex flex-wrap gap-4">
            <a
              href="https://instagram.com/liliumakan"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-pink-500 hover:text-pink-400 transition-all"
            >
              <Instagram className="w-4 h-4" />
              @liliumakan
            </a>
            
            <a
              href="https://instagram.com/academyofspirits"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-violet-500 hover:text-violet-400 transition-all"
            >
              <Instagram className="w-4 h-4" />
              @academyofspirits
            </a>
            
            <a
              href="https://instagram.com/kabileyadamu"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-cyan-500 hover:text-cyan-400 transition-all"
            >
              <Instagram className="w-4 h-4" />
              @kabileyadamu
            </a>
          </div>
        </div>
        
        {/* Website Link */}
        <div className="text-center">
          <a
            href="https://academy-of-sp-r-ts.my.canva.site/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            academy-of-sp-r-ts.my.canva.site
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default AboutPage;
